'use client'

import { useState, useEffect, type ReactNode, Fragment } from 'react'
import Overview from '@/components/ui/Overview'
import Sheet from '@/components/ui/Sheet'
import Link from '@/components/ui/Link'
import Image from 'next/image'
import { buildMediaUrl } from '@/collections/Media/utils'
import { UserRole } from '@/collections/Users/constants'
import { cn } from '@/lib/utils'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import { queryPayload } from '@/lib/payloadClient'
import { createLogger } from '@/lib/logger'
import type { User } from '@/payload-types'

const logger = createLogger('Authors')

type AuthorProps = Pick<User, 'id' | 'name' | 'image' | 'authorDescription' | 'headshot'>

type AuthorsProps = AuthorConfigProps & {
  authors?: AuthorProps[]
  className?: string
}

type AuthorProfileProps = AuthorConfigProps & {
  author: AuthorProps
}

type AuthorConfigProps = {
  inline?: boolean
  condensed?: boolean
  nested?: boolean
  theme?: 'dark' | 'light'
  // Load the headshot eagerly (not lazily) when the byline is above the fold.
  // Uses loading="eager" rather than priority: this 24px avatar is not the LCP
  // element, so we avoid fetchpriority="high" competing with the hero for bandwidth.
  eagerImage?: boolean
}

type AuthorOverviewProps = AuthorProfileProps & {
  sharedContent: ReactNode
}

export default function Authors({
  authors,
  theme,
  condensed,
  inline = false,
  className,
  eagerImage,
}: AuthorsProps) {
  const { md } = useBreakpoint()
  const [fetchedAuthors, setFetchedAuthors] = useState<AuthorProps[] | undefined>()
  const [sheetVisibleMap, setSheetVisibleMap] = useState<Record<string, boolean>>({})
  const authorsResolved = authors?.length ? authors : fetchedAuthors

  useEffect(() => {
    if (!authors?.length) {
      async function fetchAndSetAuthors() {
        try {
          const { docs: users } = await queryPayload({
            collection: 'users',
            select: {
              name: true,
              image: true,
              headshot: true,
              authorDescription: true,
              roles: true,
            },
            populate: {
              media: {
                url: true,
                alt: true,
                filename: true,
              },
            },
            where: { roles: { equals: UserRole.Author } },
            depth: 1,
            pagination: false,
            sort: ['name'],
            cacheKey: `query:users:authors`,
          })

          setFetchedAuthors(users)
        } catch (error) {
          logger.error('Failed to fetch authors', { error })
        }
      }
      fetchAndSetAuthors()
    }
  }, [authors])

  if (!authorsResolved?.length) return null

  const sharedContent = (
    <p>
      Written by our expert{authorsResolved.length > 1 ? 's' : ''}{' '}
      {authorsResolved.length === 1 ? (
        <span>{authorsResolved[0].name ?? 'Author'}</span>
      ) : (
        authorsResolved.map((author, i) => {
          const isLast = i === authorsResolved.length - 1
          const needsComma = i < authorsResolved.length - 2
          return (
            <span key={author.id}>
              {isLast ? 'and ' : ''}
              {author.name ?? 'Author'}
              {!isLast && needsComma ? ', ' : !isLast ? ' ' : ''}
            </span>
          )
        })
      )}
      . Learn more about our{' '}
      <Link className="underlined primary inline-flex" href="/criteria-and-methodology">
        criteria and methodology
      </Link>
      .
    </p>
  )

  return (
    <div
      className={cn(
        inline ? 'inline-flex gap-1' : condensed ? 'inline-flex gap-0' : 'flex gap-3',
        className,
      )}
    >
      {authorsResolved.map((author, index) => {
        const content = (
          <AuthorOverview
            author={author}
            sharedContent={sharedContent}
            theme={theme}
            inline={inline}
          />
        )

        const profile = (author: AuthorProps) => (
          <AuthorProfile
            author={author}
            inline={inline}
            condensed={condensed}
            theme={theme}
            // Only the first couple of headshots are above the fold; eager-load
            // just those and let the rest lazy-load to protect LCP.
            eagerImage={eagerImage && index < 2}
          />
        )
        const isLast = index === authorsResolved.length - 1
        const isSecondToLast = index === authorsResolved.length - 2

        return (
          <Fragment key={author.id}>
            {!md ? (
              <Sheet
                trigger={
                  <div
                    onClick={() => setSheetVisibleMap((prev) => ({ ...prev, [author.id]: true }))}
                  >
                    {profile(author)}
                  </div>
                }
                isVisible={!!sheetVisibleMap[author.id]}
                isMenu={true}
                close={() => setSheetVisibleMap((prev) => ({ ...prev, [author.id]: false }))}
              >
                <div className="px-3 pb-3">{content}</div>
              </Sheet>
            ) : (
              <Overview content={content}>{profile(author)}</Overview>
            )}
            {inline &&
              !isLast &&
              (authorsResolved.length === 2 ? ' and ' : isSecondToLast ? ' and ' : ', ')}
          </Fragment>
        )
      })}
    </div>
  )
}

export function AuthorOverview({ author, sharedContent, theme, inline }: AuthorOverviewProps) {
  const { md } = useBreakpoint()

  const authorContent = () => {
    if (author.authorDescription) {
      const authorName = author.name?.split(' ')[0] ?? 'Author'
      const description = author.authorDescription

      const safeName = authorName?.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      const regex = new RegExp(`(${safeName}(?:'s)?)`, 'g')
      const parts = description.split(regex)
      const hasMatch = parts.length > 1

      return (
        <div className="flex flex-col items-center gap-1.5">
          {(inline || !md) && <AuthorProfile author={author} theme={theme} nested={true} />}
          <p className="text-balance text-center">
            {hasMatch ? (
              <>
                {parts.map((part, i) =>
                  part === authorName || part === `${authorName}'s` ? (
                    <span key={i} className="font-medium text-gray-950 dark:text-white">
                      {part}
                    </span>
                  ) : (
                    part
                  ),
                )}
              </>
            ) : (
              description
            )}{' '}
            <Link className="underlined primary inline-flex" href="/about">
              Learn more
            </Link>
          </p>
        </div>
      )
    }

    return sharedContent
  }

  return (
    <div
      className={cn(
        'w-full px-4 py-3 text-left text-14 md:w-[305px] md:rounded-xl md:border md:shadow-xl [&>p>span]:font-semibold',
        theme === 'dark'
          ? 'bg-black text-zinc-500 md:border-white/15 [&>p>span]:text-white'
          : 'bg-white text-gray-600 dark:border-white/15 dark:bg-black dark:text-zinc-500 md:border-gray-200 [&>p>span]:text-gray-950 dark:[&>p>span]:text-white',
      )}
    >
      {authorContent()}
    </div>
  )
}

export function AuthorProfile({
  author,
  inline,
  condensed,
  theme,
  nested,
  eagerImage,
}: AuthorProfileProps) {
  const fullName = author.name?.trim()
  const firstName = fullName?.split(/\s+/)[0]
  const displayName = inline ? firstName || 'Author' : fullName || 'Author'

  const uploadedHeadshot =
    author.headshot && typeof author.headshot !== 'number' ? author.headshot : null
  const uploadedHeadshotUrl =
    uploadedHeadshot?.url ||
    (uploadedHeadshot?.filename ? buildMediaUrl(uploadedHeadshot.filename) : null)
  const authorImage = uploadedHeadshotUrl || author.image || null
  const authorImageAlt = uploadedHeadshot?.alt || author.name || 'Author'

  const Outer = inline ? 'span' : 'div'
  const Inner = inline ? 'span' : 'div'
  const ImageWrapper = inline ? 'span' : 'div'

  return (
    <Outer>
      <Inner
        className={cn(
          'flex cursor-default items-center transition-colors duration-300',
          inline
            ? ''
            : `gap-5 rounded-full py-1 pl-1 ${
                !inline && !nested
                  ? theme === 'dark'
                    ? 'hover:bg-white/15'
                    : 'hover:bg-gray-100 dark:hover:bg-white/15'
                  : ''
              } ${condensed ? 'pr-1' : 'pr-3'}`,
        )}
      >
        <Inner className={cn('flex items-center', condensed ? 'gap-1.5' : 'gap-3')}>
          {!inline && (
            <ImageWrapper
              className={cn(
                'h-6 w-6 overflow-hidden rounded-full border',
                theme === 'dark'
                  ? 'border-gray-800 bg-gray-950'
                  : 'border-gray-200 bg-gray-50 dark:border-gray-800 dark:bg-gray-950',
              )}
            >
              {authorImage && (
                <Image
                  src={authorImage}
                  alt={authorImageAlt}
                  width={24}
                  height={24}
                  loading={eagerImage ? 'eager' : undefined}
                />
              )}
            </ImageWrapper>
          )}
          {!condensed && (
            <span
              className={cn(
                'font-medium',
                inline ? 'dotted-underline' : 'text-13',
                theme === 'dark' ? 'text-white' : 'text-gray-950 dark:text-white',
              )}
            >
              {displayName}
            </span>
          )}
        </Inner>
      </Inner>
    </Outer>
  )
}
